import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "menu", "menuBtn", "menuWrap",
    "keyModal", "keyTitle", "keyDocs", "keyInput", "keyStatus", "keySubmit",
    "bridgeModal", "bridgeTitle", "bridgeHint", "bridgeCommand", "bridgeStatus", "bridgeSubmit",
    "oauthModal", "oauthUrl", "oauthCode", "oauthStatus", "oauthSubmit"
  ]
  static values = {
    createUrl: String,
    verifyUrl: String,
    bridgeUrl: String,
    oauthStartUrl: String,
    oauthPollUrl: String,
    csrf: String
  }

  connect() {
    this.provider = null
    this.onDocClick = (event) => {
      if (!this.hasMenuTarget || this.menuTarget.hidden) return
      if (this.hasMenuWrapTarget && this.menuWrapTarget.contains(event.target)) return
      this.menuTarget.hidden = true
    }
    document.addEventListener("click", this.onDocClick)
  }

  disconnect() {
    document.removeEventListener("click", this.onDocClick)
    this.stopOauthPoll()
  }

  toggleMenu(event) {
    event.stopPropagation()
    this.menuTarget.hidden = !this.menuTarget.hidden
  }

  copySnippet(event) {
    const card = event.currentTarget.closest(".models__mcp-card")
    const snippet = card?.querySelector(".models__mcp-pre")?.textContent
    if (!snippet) return

    const button = event.currentTarget
    const restore = button.textContent
    const done = () => {
      button.textContent = "복사됨"
      window.setTimeout(() => { button.textContent = restore }, 1500)
    }

    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(snippet).then(done).catch(() => {})
      return
    }

    const field = document.createElement("textarea")
    field.value = snippet
    document.body.appendChild(field)
    field.select()
    document.execCommand("copy")
    field.remove()
    done()
  }

  openKey(event) {
    const button = event.currentTarget
    this.provider = button.dataset.provider
    this.menuTarget.hidden = true
    this.keyTitleTarget.textContent = `${button.dataset.name} 연결`
    this.keyDocsTarget.href = button.dataset.docs || "#"
    this.keyInputTarget.value = ""
    this.keyInputTarget.placeholder = button.dataset.hint || "키 입력"
    this.setStatus(this.keyStatusTarget, "")
    this.keyModalTarget.hidden = false
    this.keyInputTarget.focus()
  }

  openBridge(event) {
    const button = event.currentTarget
    this.provider = button.dataset.provider
    this.menuTarget.hidden = true
    this.bridgeTitleTarget.textContent = `${button.dataset.name} 연결`
    this.bridgeHintTarget.textContent = button.dataset.hint || ""
    this.bridgeCommandTarget.hidden = true
    this.bridgeCommandTarget.textContent = ""
    this.setStatus(this.bridgeStatusTarget, "")
    this.bridgeSubmitTarget.hidden = false
    this.bridgeModalTarget.hidden = false
  }

  openOauth() {
    this.menuTarget.hidden = true
    this.stopOauthPoll()
    this.oauthCodeTarget.textContent = ""
    this.oauthUrlTarget.href = "#"
    this.setStatus(this.oauthStatusTarget, "")
    this.oauthSubmitTarget.hidden = false
    this.oauthModalTarget.hidden = false
  }

  closeModals() {
    this.stopOauthPoll()
    this.keyModalTarget.hidden = true
    this.bridgeModalTarget.hidden = true
    if (this.hasOauthModalTarget) this.oauthModalTarget.hidden = true
  }

  async startOauth() {
    this.oauthSubmitTarget.disabled = true
    this.setStatus(this.oauthStatusTarget, "로그인 주소를 준비하는 중...")
    try {
      const response = await fetch(this.oauthStartUrlValue, {
        method: "POST",
        headers: this.headers(),
        body: JSON.stringify({})
      })
      const data = await response.json()
      if (!response.ok || !data.success) {
        this.setStatus(this.oauthStatusTarget, data.message || "로그인을 시작하지 못했어요.", "error")
        return
      }
      this.oauthCodeTarget.textContent = data.user_code || ""
      this.oauthUrlTarget.href = data.verification_uri || "#"
      this.setStatus(this.oauthStatusTarget, "브라우저에서 코드를 입력한 뒤 이 창을 유지하세요.")
      this.oauthSubmitTarget.hidden = true
      window.open(data.verification_uri, "_blank", "noopener")
      this.pollOauth(Math.max(Number(data.interval) || 5, 3))
    } catch (_) {
      this.setStatus(this.oauthStatusTarget, "네트워크 오류가 발생했습니다.", "error")
    } finally {
      this.oauthSubmitTarget.disabled = false
    }
  }

  pollOauth(intervalSec) {
    this.stopOauthPoll()
    this.oauthTimer = setInterval(async () => {
      try {
        const response = await fetch(this.oauthPollUrlValue, {
          method: "POST",
          headers: this.headers(),
          body: JSON.stringify({})
        })
        const data = await response.json()
        if (data.status === "authorized") {
          this.stopOauthPoll()
          this.setStatus(this.oauthStatusTarget, data.message, "ok")
          window.location.reload()
          return
        }
        if (data.status === "pending") return
        this.stopOauthPoll()
        this.setStatus(this.oauthStatusTarget, data.message || "로그인이 끝나지 않았어요.", "error")
        this.oauthSubmitTarget.hidden = false
      } catch (_) {
        this.stopOauthPoll()
        this.setStatus(this.oauthStatusTarget, "네트워크 오류가 발생했습니다.", "error")
      }
    }, intervalSec * 1000)
  }

  stopOauthPoll() {
    if (this.oauthTimer) {
      clearInterval(this.oauthTimer)
      this.oauthTimer = null
    }
  }

  async saveKey() {
    const apiKey = this.keyInputTarget.value.trim()
    if (apiKey.length < 8) {
      this.setStatus(this.keyStatusTarget, "키를 입력해 주세요.", "error")
      return
    }
    this.keySubmitTarget.disabled = true
    this.setStatus(this.keyStatusTarget, "확인 중...")
    try {
      const response = await fetch(this.createUrlValue, {
        method: "POST",
        headers: this.headers(),
        body: JSON.stringify({ provider: this.provider, api_key: apiKey })
      })
      const data = await response.json()
      if (!response.ok || !data.success) {
        this.setStatus(this.keyStatusTarget, data.message || "연결에 실패했습니다.", "error")
        return
      }
      this.setStatus(this.keyStatusTarget, data.message, "ok")
      window.location.reload()
    } catch (_) {
      this.setStatus(this.keyStatusTarget, "네트워크 오류가 발생했습니다.", "error")
    } finally {
      this.keySubmitTarget.disabled = false
    }
  }

  async saveBridge() {
    this.bridgeSubmitTarget.disabled = true
    this.setStatus(this.bridgeStatusTarget, "연결 준비 중...")
    try {
      const response = await fetch(this.bridgeUrlValue, {
        method: "POST",
        headers: this.headers(),
        body: JSON.stringify({ provider: this.provider })
      })
      const data = await response.json()
      if (!response.ok || !data.success) {
        this.setStatus(this.bridgeStatusTarget, data.message || "연결에 실패했습니다.", "error")
        return
      }
      this.bridgeCommandTarget.hidden = false
      this.bridgeCommandTarget.textContent = data.install_command || ""
      this.setStatus(this.bridgeStatusTarget, data.message, "ok")
      this.bridgeSubmitTarget.hidden = true
    } catch (_) {
      this.setStatus(this.bridgeStatusTarget, "네트워크 오류가 발생했습니다.", "error")
    } finally {
      this.bridgeSubmitTarget.disabled = false
    }
  }

  headers() {
    return {
      "Content-Type": "application/json",
      "X-CSRF-Token": this.csrfValue,
      "Accept": "application/json"
    }
  }

  setStatus(el, message, kind) {
    if (!el) return
    el.textContent = message || ""
    el.classList.toggle("is-error", kind === "error")
    el.classList.toggle("is-ok", kind === "ok")
  }
}
